import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import ArtDirectionImage, {
  type ArtDirectionImageProps,
} from '@/app/components/Image/ArtDirectionImage';
import RichText from '@/app/components/RichText';

interface SimpleHeroProps {
  readonly additionalContent?: React.ReactNode;
  readonly artDirectionImageProps: ArtDirectionImageProps;
  readonly eyebrowMessageKey: MessageKey;
  readonly 'data-cy'?: string;
  readonly textColor?: 'light' | 'dark';
  readonly titleMessageKey: MessageKey;
}

const SimpleHero = ({
  additionalContent,
  artDirectionImageProps,
  eyebrowMessageKey,
  'data-cy': dataCy = 'simple-hero',
  textColor = 'light',
  titleMessageKey,
}: SimpleHeroProps) => {
  const t = useTranslations();

  return (
    <section className="relative h-screen lg:h-[540px]" data-cy={dataCy}>
      <div className="absolute inset-0">
        <LayoutGrid className="size-full">
          <GridItem
            colEnd={{ sm: 'full' }}
            colStart={{ sm: 'full' }}
            rowEnd={{ sm: 2 }}
            rowStart={{ sm: 1 }}
          >
            <div className="mx-auto size-full max-w-[1560px] lg:px-5">
              <div className="relative size-full overflow-hidden lg:rounded-b-lg">
                <ArtDirectionImage
                  className="object-cover"
                  fill
                  {...artDirectionImageProps}
                />
              </div>
            </div>
          </GridItem>
          <GridItem
            className="relative z-10 flex h-full flex-col pt-36 pb-12 max-lg:items-center"
            colEnd={{ sm: 'main' }}
            colStart={{ sm: 'main' }}
            rowEnd={{ sm: 2 }}
            rowStart={{ sm: 1 }}
          >
            <Typography
              className="mb-2 tracking-wider uppercase lg:mb-6"
              color={textColor}
              Element="h1"
              weight="bold"
            >
              {t(eyebrowMessageKey)}
            </Typography>
            <Typography
              align="centerLgLeft"
              className="mb-10 leading-tighter lg:mb-auto"
              color={textColor}
              Element="h2"
              variant="h2"
            >
              <RichText messageKey={titleMessageKey} />
            </Typography>
            {additionalContent}
          </GridItem>
        </LayoutGrid>
      </div>
    </section>
  );
};

export default SimpleHero;
